/**
 * MantenimientosTable — DataTable reutilizable para listar mantenimientos.
 */
import React, { useState, useMemo } from 'react';
import { Box, Typography, TextField, InputAdornment, Paper, Chip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BuildIcon from '@mui/icons-material/Build';
import DataTable from '../../common/DataTable';
import EmptyState from '../../common/EmptyState';
import { ESTADOS, CATEGORIAS, fmtFecha, diasParaVencer, fmtMoney } from './format';

export default function MantenimientosTable({
  rows, loading, onRowClick, emptyTitle, emptyMessage,
  showEmbarcacion = true, showVencimiento = true, showProveedor = false, showCosto = false,
  defaultSortBy = 'fecha_programada', defaultSortDir = 'asc',
}) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => (
      String(r.nombre || '').toLowerCase().includes(q) ||
      String(r.embarcacion_nombre || '').toLowerCase().includes(q) ||
      String(r.proveedor || '').toLowerCase().includes(q) ||
      String(r.categoria || '').toLowerCase().includes(q)
    ));
  }, [rows, search]);

  const columns = [];

  columns.push({
    id: 'tarea', label: 'Tarea', sortable: true, sortValue: r => r.nombre || '',
    render: (r) => {
      const cat = CATEGORIAS.find(c => c.value === r.categoria) || CATEGORIAS[CATEGORIAS.length - 1];
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 4, height: 22, borderRadius: 1, bgcolor: cat.color }} />
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{r.nombre}</Typography>
            <Typography sx={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase' }}>
              {cat.label} · {r.tipo_evento}
              {r.bloquea_operacion && ' · ⚠ Bloquea'}
            </Typography>
          </Box>
        </Box>
      );
    },
  });

  if (showEmbarcacion) {
    columns.push({
      id: 'embarcacion', label: 'Embarcación', sortable: true, sortValue: r => r.embarcacion_nombre || '',
      render: (r) => (
        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{r.embarcacion_nombre || '—'}</Typography>
      ),
    });
  }

  if (showVencimiento) {
    columns.push({
      id: 'fecha', label: 'Fecha programada', sortable: true, sortValue: r => r.fecha_programada || '',
      render: (r) => {
        const dias = diasParaVencer(r.fecha_programada);
        const isVencido = dias !== null && dias < 0;
        const isUrgente = dias !== null && dias >= 0 && dias <= 7;
        return (
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{fmtFecha(r.fecha_programada)}</Typography>
            <Typography sx={{
              fontSize: 10, fontWeight: 700,
              color: isVencido ? '#dc2626' : isUrgente ? '#f59e0b' : '#64748b',
            }}>
              {dias === null ? '—' :
                dias < 0 ? `Vencido hace ${Math.abs(dias)} días` :
                dias === 0 ? 'Hoy' :
                dias === 1 ? 'Mañana' :
                `En ${dias} días`}
            </Typography>
          </Box>
        );
      },
    });
  }

  if (showProveedor) {
    columns.push({
      id: 'proveedor', label: 'Proveedor',
      render: (r) => <Typography sx={{ fontSize: 12 }}>{r.proveedor || '—'}</Typography>,
    });
  }

  if (showCosto) {
    columns.push({
      id: 'costo', label: 'Costo', align: 'right', sortable: true, sortValue: r => Number(r.costo || 0),
      render: (r) => (
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0d9488' }}>
          {fmtMoney(r.costo)}
        </Typography>
      ),
    });
  }

  columns.push({
    id: 'estado', label: 'Estado',
    render: (r) => {
      const c = ESTADOS[r.estado] || ESTADOS.programado;
      return <Chip size="small" label={c.label}
        sx={{ bgcolor: c.bg, color: c.color, fontWeight: 700, fontSize: 11, height: 22 }} />;
    },
  });

  return (
    <Box>
      <Box sx={{ mb: 1.5 }}>
        <TextField fullWidth size="small"
          placeholder="Buscar por tarea, embarcación, proveedor…"
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
              </InputAdornment>
            ),
            sx: { bgcolor: '#fff', borderRadius: 2, fontSize: 13 },
          }} />
      </Box>

      {!loading && filtered.length === 0 ? (
        <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(20px)' }}>
          <EmptyState icon={<BuildIcon sx={{ fontSize: 56, color: '#cbd5e1' }} />}
            title={emptyTitle || 'Sin mantenimientos'}
            message={emptyMessage || (search ? 'No hay coincidencias con tu búsqueda.' : 'Aún no hay registros en esta categoría.')} />
        </Paper>
      ) : (
        <DataTable columns={columns} rows={filtered} loading={loading}
          rowKey={r => r.id}
          defaultSortBy={defaultSortBy} defaultSortDir={defaultSortDir}
          onRowClick={onRowClick} />
      )}
    </Box>
  );
}
